// frontend/src/types/shareLinks.ts
// Public share links (docs/plans/2026-10-28-tank-shots-implementation.md, section 5;
// design 8.2-8.6). Two kinds: two tank shots compared, and two photos of one
// organism compared (docs/plans/2026-10-29-livestock-compare-plan.md, 7.2 and 7.5).

import type { LivestockType } from './livestock';
import type { TankPhotoFraming } from './tankPhotos';

/** The backend's limit on a link's title (ShareLinkCreate.title). */
export const SHARE_TITLE_MAX = 120;

export type ShareLinkKind = 'tank_compare' | 'organism_compare';

export interface TankCompareTarget {
  before_id: number;
  after_id: number;
}

/** Two photo entries of one organism of the link's tank. */
export interface OrganismCompareTarget {
  livestock_id: number;
  before_id: number;
  after_id: number;
}

interface ShareLinkBase {
  id: number;
  /** https://<host>/s/<token>: a bearer link, never persisted or sent to analytics. */
  url: string;
  title: string | null;
  /** ISO 8601 */
  created_at: string;
  /** YYYY-MM-DD in the owner's time zone; null when that photo was deleted (the link is broken). */
  before_date: string | null;
  after_date: string | null;
}

/** A live tank shots link as its owner sees it (exactly today's keys). */
export interface TankCompareShareLink extends ShareLinkBase {
  kind: 'tank_compare';
  show_tank_name: boolean;
  target: TankCompareTarget;
}

/** A live organism photos link as its owner sees it. */
export interface OrganismCompareShareLink extends ShareLinkBase {
  kind: 'organism_compare';
  show_subject_name: boolean;
  show_scientific_name: boolean;
  target: OrganismCompareTarget;
  livestock_id: number;
  /** The organism's name, for the owner only; null once the organism is deleted. */
  subject_name: string | null;
}

/** A live link as its owner sees it, by kind. */
export type ShareLink = TankCompareShareLink | OrganismCompareShareLink;

export interface ShareLinkList {
  items: ShareLink[];
}

export interface TankCompareShareLinkCreate {
  kind: 'tank_compare';
  target: TankCompareTarget;
  title?: string | null;
  show_tank_name?: boolean;
}

/** The server refuses show_tank_name on an organism link (and the name switches on a tank one). */
export interface OrganismCompareShareLinkCreate {
  kind: 'organism_compare';
  target: OrganismCompareTarget;
  title?: string | null;
  show_subject_name?: boolean;
  show_scientific_name?: boolean;
}

export type ShareLinkCreate = TankCompareShareLinkCreate | OrganismCompareShareLinkCreate;

/** POST answers 201 with a new link, or 200 with the live link this pair already has. */
export interface ShareLinkCreated {
  link: ShareLink;
  created: boolean;
}

export interface RevokeAllResponse {
  revoked: number;
}

export type PublicShareVariant = 'thumb' | 'display' | 'full';
export type PublicShareSlotName = 'before' | 'after';

export interface PublicShareSlot {
  /** YYYY-MM-DD, a date only */
  date: string;
  /** Paths on the public image route, /api/public/share/<token>/image/<slot>/<variant> */
  images: Record<PublicShareVariant, string>;
  /**
   * How the shot is lined up, without ids (docs/plans/2026-10-29-tank-shot-framing-design.md,
   * 8.1); null shows it as taken. Optional: an older backend leaves it out.
   */
  framing?: Omit<TankPhotoFraming, 'ref_photo_id'> | null;
}

interface PublicShareBase {
  title: string | null;
  /** Only when the owner chose to show it (always null for an organism link). */
  tank_name: string | null;
  /** YYYY-MM-DD */
  created_on: string;
  slots: Record<PublicShareSlotName, PublicShareSlot>;
}

export interface PublicTankShare extends PublicShareBase {
  kind: 'tank_compare';
}

export interface PublicOrganismShare extends PublicShareBase {
  kind: 'organism_compare';
  /** The organism's common name, only when the owner chose to show it. */
  subject_name: string | null;
  /** Its scientific name, only when the owner chose to show it. */
  scientific_name: string | null;
  /** One of the four types, or null. */
  livestock_type: LivestockType | null;
}

/** GET /api/public/share/{token}: everything a viewer gets. */
export type PublicShare = PublicTankShare | PublicOrganismShare;
